/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   LakehouseSizeCell.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (T159 item 4)
 *     Description     :   The lakehouse Size cell, shared by the Lakehouses list and the Lakehouse Catalog. When the
 *                         last collection did not size a lakehouse (size_measurement_error set) it reads "Not
 *                         measured", and its tooltip names the last size dbo.lakehouse_snapshots recorded and the
 *                         date. It never shows an old size as current or "Empty". On dev on 2026-10-22, 130 of
 *                         PersonalMed's 132 lakehouses were not measured: 79 had shown an old size, 51 "Empty".
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { AppTooltip } from '@/components/ui';
import { formatEasternDate } from '@/utils/dateFormatter';
import { formatEntitySize, humanBytes } from '../../utils/entitySize';

export interface LakehouseSizeCellProps {
  sizeBytes: number | null | undefined;
  tableCount: number | null | undefined;
  sizeGb: number | null | undefined;
  sizeError: string | null | undefined;
  lastRecordedGb: number | null | undefined;
  /** YYYY-MM-DD of the last snapshot that recorded a size. */
  lastRecordedOn: string | null | undefined;
}

/** The tooltip for a lakehouse the last collection did not size. */
export function lakehouseSizeNote(reason: string, lastRecordedGb: number | null | undefined, lastRecordedOn: string | null | undefined): string {
  const head = `The last collection did not measure this lakehouse (${reason}).`;
  if (lastRecordedGb == null || !lastRecordedOn) return `${head} No size has been recorded for it.`;
  // A date-only value parsed as UTC midnight would read as the previous day in Eastern time; noon keeps the date.
  const on = formatEasternDate(`${lastRecordedOn}T12:00:00Z`);
  return `${head} Last recorded size: ${humanBytes(lastRecordedGb * 1024 ** 3)} on ${on}.`;
}

export const LakehouseSizeCell: React.FC<LakehouseSizeCellProps> = ({
  sizeBytes, tableCount, sizeGb, sizeError, lastRecordedGb, lastRecordedOn,
}) => {
  const size = formatEntitySize(sizeBytes, tableCount, sizeGb, sizeError);
  if (size.notMeasured) {
    return (
      <AppTooltip title={lakehouseSizeNote(String(sizeError), lastRecordedGb, lastRecordedOn)}>
        <span className="text-[var(--color-text-secondary)]" data-testid="size-not-measured">Not measured</span>
      </AppTooltip>
    );
  }
  if (size.isEmpty) {
    return (
      <AppTooltip title="No tables — this lakehouse holds no data">
        <span className="text-[var(--color-text-secondary)]">Empty</span>
      </AppTooltip>
    );
  }
  const tables = tableCount ?? 0;
  return (
    <AppTooltip title={`${tables} table${tables === 1 ? '' : 's'}${size.bytes != null ? ` · ${size.bytes.toLocaleString()} bytes` : ''}`}>
      <span className="tabular-nums text-[var(--color-text-primary)]">{size.text}</span>
    </AppTooltip>
  );
};

export default LakehouseSizeCell;
